import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Checkbox } from '@/components/ui/checkbox'
import { useToast } from '@/components/ui/use-toast'
import { Download, FileText, FileSpreadsheet, FileJson } from 'lucide-react'
import { format, subDays } from 'date-fns'
import type { Device } from '@/types/device.types'
import { measurementService } from '@/services/measurement.service'

interface DataExportDialogProps {
  device: Device
}

type ExportFormat = 'csv' | 'excel' | 'json'

export const DataExportDialog = ({ device }: DataExportDialogProps) => {
  const { toast } = useToast()
  const [open, setOpen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [exportFormat, setExportFormat] = useState<ExportFormat>('csv')
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'))
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'))

  const [includeFields, setIncludeFields] = useState({
    timestamp: true,
    liquidLevel: true,
    temperature: true,
    humidity: true,
    battery: true,
    solar: true,
    rain: true,
    wind: true,
    pressure: true,
    soilMoisture: true,
    location: true,
  })

  const toggleField = (field: keyof typeof includeFields) => {
    setIncludeFields(prev => ({ ...prev, [field]: !prev[field] }))
  }

  const handleExport = async () => {
    setIsExporting(true)

    try {
      const selectedFields = Object.entries(includeFields)
        .filter(([, checked]) => checked)
        .map(([key]) => key)

      // Backend only supports csv/json (not excel yet)
      const apiFormat = exportFormat === 'excel' ? 'csv' : exportFormat

      const blob = await measurementService.exportMeasurements(
        device.id,
        apiFormat,
        startDate,
        endDate,
        selectedFields
      )

      // Trigger browser download
      const extension = exportFormat === 'excel' ? 'csv' : exportFormat
      const filename = `${device.name.replace(/\s+/g, '_')}_${format(new Date(), 'yyyy-MM-dd')}.${extension}`
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      a.remove()

      toast({
        title: 'Export Complete',
        description: `Downloaded ${filename}`,
      })

      setOpen(false)
    } catch (error) {
      toast({
        title: 'Export Failed',
        description: 'Failed to export measurement data. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsExporting(false)
    }
  }

  const deviceType = device.deviceType?.toLowerCase()

  // Determine which fields are relevant for this device type
  const isWaterSense = deviceType?.includes('watersense') || deviceType?.includes('water')
  const isRK900 = deviceType?.includes('rk900') || deviceType?.includes('900')
  const isDHT11 = deviceType?.includes('dht11') || deviceType?.includes('dht') || deviceType?.includes('soil')

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Download className="mr-2 h-4 w-4" />
          Export Data
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Export Measurement Data</DialogTitle>
          <DialogDescription>
            Export device measurements to CSV, Excel, or JSON format
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Export Format */}
          <div className="space-y-3">
            <Label>Export Format</Label>
            <RadioGroup value={exportFormat} onValueChange={(value) => setExportFormat(value as ExportFormat)}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="csv" id="csv" />
                <Label htmlFor="csv" className="flex items-center gap-2 font-normal cursor-pointer">
                  <FileText className="h-4 w-4" />
                  CSV (Comma-Separated Values)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="excel" id="excel" />
                <Label htmlFor="excel" className="flex items-center gap-2 font-normal cursor-pointer">
                  <FileSpreadsheet className="h-4 w-4" />
                  Excel (XLSX)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="json" id="json" />
                <Label htmlFor="json" className="flex items-center gap-2 font-normal cursor-pointer">
                  <FileJson className="h-4 w-4" />
                  JSON (JavaScript Object Notation)
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Date Range */}
          <div className="space-y-3">
            <Label>Date Range</Label>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start-date" className="text-xs text-muted-foreground">Start Date</Label>
                <input
                  id="start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm border rounded-md"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end-date" className="text-xs text-muted-foreground">End Date</Label>
                <input
                  id="end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm border rounded-md"
                />
              </div>
            </div>
          </div>

          {/* Field Selection */}
          <div className="space-y-3">
            <Label>Include Fields</Label>
            <div className="grid grid-cols-2 gap-3 max-h-[200px] overflow-y-auto p-3 border rounded-md">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="timestamp"
                  checked={includeFields.timestamp}
                  onCheckedChange={() => toggleField('timestamp')}
                />
                <Label htmlFor="timestamp" className="font-normal cursor-pointer">
                  Timestamp
                </Label>
              </div>

              {isWaterSense && (
                <>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="liquidLevel"
                      checked={includeFields.liquidLevel}
                      onCheckedChange={() => toggleField('liquidLevel')}
                    />
                    <Label htmlFor="liquidLevel" className="font-normal cursor-pointer">
                      Water Level
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="rain"
                      checked={includeFields.rain}
                      onCheckedChange={() => toggleField('rain')}
                    />
                    <Label htmlFor="rain" className="font-normal cursor-pointer">
                      Rainfall
                    </Label>
                  </div>
                </>
              )}

              {(isWaterSense || isRK900) && (
                <>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="temperature"
                      checked={includeFields.temperature}
                      onCheckedChange={() => toggleField('temperature')}
                    />
                    <Label htmlFor="temperature" className="font-normal cursor-pointer">
                      Temperature
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="humidity"
                      checked={includeFields.humidity}
                      onCheckedChange={() => toggleField('humidity')}
                    />
                    <Label htmlFor="humidity" className="font-normal cursor-pointer">
                      Humidity
                    </Label>
                  </div>
                </>
              )}

              {isRK900 && (
                <>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="wind"
                      checked={includeFields.wind}
                      onCheckedChange={() => toggleField('wind')}
                    />
                    <Label htmlFor="wind" className="font-normal cursor-pointer">
                      Wind Speed/Direction
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="pressure"
                      checked={includeFields.pressure}
                      onCheckedChange={() => toggleField('pressure')}
                    />
                    <Label htmlFor="pressure" className="font-normal cursor-pointer">
                      Atmospheric Pressure
                    </Label>
                  </div>
                </>
              )}

              {isDHT11 && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="soilMoisture"
                    checked={includeFields.soilMoisture}
                    onCheckedChange={() => toggleField('soilMoisture')}
                  />
                  <Label htmlFor="soilMoisture" className="font-normal cursor-pointer">
                    Soil Moisture
                  </Label>
                </div>
              )}

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="battery"
                  checked={includeFields.battery}
                  onCheckedChange={() => toggleField('battery')}
                />
                <Label htmlFor="battery" className="font-normal cursor-pointer">
                  Battery Level
                </Label>
              </div>

              {(isWaterSense || isRK900) && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="solar"
                    checked={includeFields.solar}
                    onCheckedChange={() => toggleField('solar')}
                  />
                  <Label htmlFor="solar" className="font-normal cursor-pointer">
                    Solar Voltage
                  </Label>
                </div>
              )}

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="location"
                  checked={includeFields.location}
                  onCheckedChange={() => toggleField('location')}
                />
                <Label htmlFor="location" className="font-normal cursor-pointer">
                  GPS Location
                </Label>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleExport} disabled={isExporting}>
            <Download className="mr-2 h-4 w-4" />
            {isExporting ? 'Exporting...' : 'Export Data'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
